import React, { useCallback } from 'react';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { IElement, OneOfListElements } from '@shared/models/project.model';
import { clearSelectionForCollapsedDescendants } from '@client/hooks/element-list-collapse.helpers';
import {
  setElementExpanded,
  useIsElementExpanded,
} from '@client/hooks/use-expand-elements.hooks';
import {
  type ElementListUrlPage,
  useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import {
  useFilteredElementChildren,
  setSortElementsBy,
} from '@client/hooks/use-filter-elements.hooks';
import { useMoveElements } from '@client/hooks/use-move-elements.hooks';
import { getUIState } from '@client/store/ui';
import Droppable, { DropType } from '@ui/drag-and-drop/Droppable';
import Draggable from '@ui/drag-and-drop/Draggable';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';
import ElementListRowConnected from './ElementListRowConnected';
import { ELEMENT_LIST_ROW_HEIGHT } from './ElementListRow';

interface ElementTreeRowProps {
  element: IElement;
  depth: number;
  disabled?: boolean;
  selectedRow: string | null;
  onSelect: (id: string) => void;
  selectionPage?: ElementListUrlPage;
  forceExpandedElementIds?: ReadonlySet<string>;
  onDrop?: (
    dragId: string,
    dropId: string | undefined,
    dropType: DropType | undefined,
  ) => void;
  validateDrop?: (
    dragId: string,
    dropId: string | undefined,
    dropType: DropType | undefined,
  ) => boolean;
}

const ElementTreeRow: React.FC<ElementTreeRowProps> = ({
  element,
  depth,
  disabled,
  selectedRow,
  onSelect,
  selectionPage,
  forceExpandedElementIds,
  onDrop: onDropProp,
  validateDrop: validateDropProp,
}) => {
  const persistedExpanded = useIsElementExpanded(element);
  const forceOpen = forceExpandedElementIds?.has(element.id) ?? false;
  const expanded = forceOpen ? true : persistedExpanded;
  const children = useFilteredElementChildren(element) as OneOfListElements[];
  const effectiveDisabled = disabled || element.isDeactivated;

  const { moveElementsTo, isMoveElementsAllowed } = useMoveElements();
  const navigateTo = useProjectNavigation();

  const handleDrop = useCallback(
    (
      dragId: string,
      dropId: string | undefined,
      dropType: DropType | undefined,
    ) => {
      if (onDropProp) {
        onDropProp(dragId, dropId, dropType);
        return;
      }
      if (dropId && dropType) {
        setSortElementsBy('position');
        getUIState().setSelectedElementId(dragId);
        if (dropType === 'inside') {
          setElementExpanded(dropId, true);
        }
        moveElementsTo(dropId, dropType, dragId);
      }
    },
    [onDropProp, moveElementsTo],
  );

  const handleValidate = useCallback(
    (
      dragId: string,
      dropId: string | undefined,
      dropType: DropType | undefined,
    ) => {
      if (validateDropProp) return validateDropProp(dragId, dropId, dropType);
      return isMoveElementsAllowed(dropId, dropType, dragId);
    },
    [validateDropProp, isMoveElementsAllowed],
  );

  const handleExpandedChange = useCallback(
    (expanded: boolean) => {
      if (!expanded) {
        clearSelectionForCollapsedDescendants(element, navigateTo);
      }
      setElementExpanded(element, expanded);
    },
    [element, navigateTo],
  );

  const handleSelect = useCallback(
    () => onSelect(element.id),
    [onSelect, element.id],
  );

  const expandableChildren = children.filter(projectGuards.is.element);
  const selected = selectedRow === element.id;

  return (
    <>
      <HideOutsideViewport
        height={ELEMENT_LIST_ROW_HEIGHT}
        alwaysVisible={selected}
      >
        <Droppable
          dropId={element.id}
          allowDropAbove
          allowDropBelow
          allowDropInside
          onDrop={handleDrop}
          validateFn={handleValidate}
          indentation={depth - 1}
        >
          <Draggable dragId={element.id}>
            <ElementListRowConnected
              element={element}
              depth={depth}
              disabled={disabled}
              variant="element"
              expandable={expandableChildren.length > 0 && !forceOpen}
              expanded={expanded}
              onExpandedChange={handleExpandedChange}
              selected={selected}
              onSelect={handleSelect}
              selectionPage={selectionPage}
            />
          </Draggable>
        </Droppable>
      </HideOutsideViewport>
      {expanded &&
        expandableChildren.map((child) => (
          <ElementTreeRow
            key={child.id}
            element={child}
            depth={depth + 1}
            disabled={effectiveDisabled}
            selectedRow={selectedRow}
            onSelect={onSelect}
            selectionPage={selectionPage}
            forceExpandedElementIds={forceExpandedElementIds}
            onDrop={onDropProp}
            validateDrop={validateDropProp}
          />
        ))}
    </>
  );
};

export default ElementTreeRow;
